import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Authorization from 'containers/Authorization';
import shouldWrapInAdmin from '../../../../../modules/widgets/shouldWrapInAdmin';
import staticWidgets from '../../../../../website/widgets/static';
import dynamicWidgets from '../../../../../website/widgets/dynamic/server';
import Placeholder from '../../../../../website/widgets/static/Placeholder';
import style from './WidgetLoader.scss';

const propTypes = {
  type: PropTypes.string.isRequired,
  payload: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  editChannel: PropTypes.string,
  onWidgetEditStart: PropTypes.func,
  onDelete: PropTypes.func,
  widgetSwitchingId: PropTypes.string,
  id: PropTypes.string.isRequired,
  onStartWidgetSwitch: PropTypes.func,
  onSwitchWidgets: PropTypes.func,
  componentId: PropTypes.string.isRequired,
};

const defaultProps = {
  payload: {},
  widgetSwitchingId: undefined,
};

const contextTypes = {
  store: PropTypes.object.isRequired,
};

const WidgetLoader = ({
  type,
  payload,
  isEditing,
  widgetSwitchingId,
  id,
  onSwitchWidgets,
  componentId,
  ...rest
}) => {
  const isSwitching = widgetSwitchingId === id;

  const widgetClass = dynamicWidgets[componentId] || staticWidgets[componentId];

  // This case happens when a widget has been completely deleted from Voyager
  // or someone forgot to add it to the exports from static/dynamic widgets
  if (!widgetClass) {
    return <Placeholder>The widget {type} is no longer available.</Placeholder>;
  }

  const widgetProps = {
    ...(widgetClass ? widgetClass.manifestInfo.defaultPayload : {}),
    ...payload,
    ...rest,
    isEditable: true,
    isBuilderView: true,
    isEditing: !!isEditing,
    onSwitchWidgets,
    id,
    componentId,
  };

  const Component = widgetClass;
  const {
    authorization,
    manifestInfo: { disallowWidgetSwitching = false },
  } = widgetClass;

  let widget;
  if (authorization) {
    widget = (
      <Authorization {...authorization}>
        {authorized => (
          <Component {...widgetProps} unauthorized={!authorized} />
        )}
      </Authorization>
    );
  } else {
    widget = <Component {...widgetProps} />;
  }

  if (!shouldWrapInAdmin(componentId)) {
    return widget;
  }

  const classNames = cx(style.root, 'clearfix', {
    [style.active]: isEditing,
    [style.canSwitch]: widgetSwitchingId,
    [style.isSwitching]: isSwitching,
    [style.disallowWidgetSwitching]:
      disallowWidgetSwitching && widgetSwitchingId,
  });

  const overlayClasses = cx(
    style.overlay,
    isSwitching ? style.selectedOverlay : style.selectableOverlay,
  );

  return (
    <div className={classNames}>
      {widget}
      <div className={overlayClasses}>
        <div className={style.message}>
          {isSwitching ? 'Selected Widget' : 'Click To Switch Widgets'}
        </div>
      </div>
    </div>
  );
};

WidgetLoader.propTypes = propTypes;
WidgetLoader.defaultProps = defaultProps;
WidgetLoader.contextTypes = contextTypes;

export default WidgetLoader;
